import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, message, Popconfirm, Layout, Select, Space, Spin, Switch, Descriptions } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, StopOutlined, CheckOutlined, BarChartOutlined, ExportOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { Content, Header } from 'antd/es/layout/layout';
import TopNav from './TopNav';
import useSetTitle from './GlobalSetTitleHook';
import { useLocation, useNavigate } from 'react-router-dom';
import ExportStatisticsModal from './ExportStatisticsModal';

interface Rule {
  id: number;
  name: string;
  description: string;
  prompt: string;
  location?: string;
  page_num?: number;
  active: boolean;
  category: string;  // 对应后端的 category 字段
  latest_version: number;
  labs: Lab[];
}

interface Lab {
  id: number;
  name: string;
  lab_code: string;
  simple_name: string;
}

interface ReportStat {
  id: number;
  report_number: string;
  review_date: string;
  result_judgement: string;
  reason: string;
  report_url: string;
  report: number;
  rule: number;
}

interface RuleVersion {
  rule_id: number;
  versions: number[];
}

const ReviewRules: React.FC = () => {
  const navigate = useNavigate();
  useSetTitle()
  const [rules, setRules] = useState<Rule[]>([]);
  const [labs, setLabs] = useState<Lab[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [currentRule, setCurrentRule] = useState<Rule | null>(null);
  // const [ruleSnapshot, setRuleSnapshot] = useState<Rule | null>(null);
  const [versions, setVersions] = useState<number[]>([]);
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({
    active: '',
    category: '',
    name: '',
    location: '',
    lab_ids: [] as number[],
  });
  const [loading, setLoading] = useState(false); // 加载状态
  const [categoryInput, setCategoryInput] = useState(filters.category);
  const [nameInput, setNameInput] = useState(filters.name);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const [statsModalVisible, setStatsModalVisible] = useState(false);
  const [statsData, setStatsData] = useState<any>(null);
  const [reportsData, setReportsData] = useState<ReportStat[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const totalReports = statsData ? statsData.total_reports : 0;
  const [promptLoading, setPromptLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    fetchRules();
    fetchLabs();
    // fetchCategories();
  }, [filters]);

  const fetchRules = async () => {
    setLoading(true); // 开始加载
    try {
      const { active, category, location, name, lab_ids } = filters;
      let url = `/api/rules/?`;
      
      if (active) url += `active=${active}&`;
      if (category) url += `category=${category}&`;
      if (location) url += `location=${location}&`;
      if (name) url += `name=${name}&`;
      if (lab_ids.length > 0) url += `lab_ids=${lab_ids.join(',')}&`;

      const response = await api.get(url);
      setRules(response.data);
    } catch (error) {
      message.error('获取规则失败');
      console.error('Failed to fetch rules:', error);
    } finally {
      setLoading(false); // 完成加载
    }
  };

  const fetchLabs = async () => {
    try {
      const response = await api.get('/api/lab/');
      setLabs(response.data);
    } catch (error) {
      message.error('获取实验室信息失败');
      console.error('Failed to fetch labs:', error);
    }
  };

  const fetchVersions = async (rule_id: number) => {
    try {
      const response = await api.get<RuleVersion>(`/api/report-rules/${rule_id}/versions`);
      setVersions(response.data.versions);
    } catch (error) {
      message.error('历史版本号失败');
      console.error('Failed to fetch versions:', error);
    }
  };

const fetchRuleSnapshot = async (rule_id: number, version: number | string) => {
  try {
    if (version === "0") {
      setEditingRule(currentRule);
      setIsReadOnly(false)
    }
    else {
      const response = await api.get<Rule>(`/api/report-rules/${rule_id}/snapshot?version=${version}`);
      const data = response.data;
      
      setIsReadOnly(true);
      setEditingRule(data); // 保存到状态，便于其他操作
    }

      // 更新 Form 的值
    form.setFieldsValue({
      name: editingRule?.name,
      description: editingRule?.description,
      category: editingRule?.category,
      prompt: editingRule?.prompt,
      location: editingRule?.location,
      page_num: editingRule?.page_num,
      active: editingRule?.active,
      // 如果还有 labs 或其他字段，也可以一并设置
      labs: editingRule?.labs?.map(l => l.id) || [],
      version: editingRule?.latest_version,
    });
  } catch (error) {
    message.error('历史版本号失败');
    console.error('Failed to fetch versions:', error);
    }
  };

  const handleGeneratePrompt = async () => {
    try {
      setPromptLoading(true);
      const values = form.getFieldsValue(['name', 'description', 'location']);
      if (!values.name || !values.description || !values.location) {
        message.warning('请先填写“名称”、“描述” 和 “报告位置”');
        return;
      }

      const response = await api.post('/api/generate-prompt/', {
        name: values.name,
        description: values.description,
        location: values.location,
      });

      form.setFieldsValue({ prompt: response.data });
      message.success('提示词生成成功');
      setPromptLoading(false);
    } catch (error) {
      console.error('生成提示词失败:', error);
      message.error('生成提示词失败');
      setPromptLoading(false);
    }
  };

  const handleToggleActive = async (record: Rule) => {
    const newStatus = !record.active;
    const actionText = newStatus ? '启用' : '禁用';

    try {
      setLoading(true);
      await api.post(`/api/rules/${record.id}/update-active/`, { active: newStatus });
      message.success(`${actionText}成功`);
      fetchRules(); // 刷新列表
    } catch (error) {
      message.error(`${actionText}失败`);
      console.error(`Failed to ${actionText} rule:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrEdit = async (values: { name: string; description: string; active: boolean }) => {
    try {
      if (editingRule) {
        await api.put(`/api/rules/${editingRule.id}/`, values);
        message.success('规则更新成功');
      } else {
        await api.post('/api/rules/', values);
        message.success('规则添加成功');
      }
      setModalVisible(false);
      form.resetFields();
      fetchRules();
    } catch (error) {
      message.error(editingRule ? '更新规则失败' : '添加规则失败');
      console.error('Failed to save rule:', error);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/api/rules/${id}/`);
      message.success('规则删除成功');
      fetchRules();
    } catch (error) {
      message.error('删除规则失败');
      console.error('Failed to delete rule:', error);
    }
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
  };

  const handleShowStats = async (ruleId:number, version:number) => {
    try {
      setReportsLoading(true);
      const statsRes = await api.get(`/api/review-rules/${ruleId}/stats/?version=${version}`);
      const reportsRes = await api.get(`/api/review-rules/${ruleId}/reports/?page=${currentPage}&page_size=${pageSize}&version=${version}`);
      setStatsData(statsRes.data);
      setReportsData(reportsRes.data.results);
      setStatsModalVisible(true);
    } catch (error) {
      message.error('获取规则统计信息失败');
      console.error(error);
    } finally {
      setReportsLoading(false);
    }
  };

  const columns = [
    {
      title: '分组',
      dataIndex: 'category',  // 新增的分组列，显示 category
      key: 'category',
      width: 180,
    },
    {
      title: '规则名称',
      dataIndex: 'name',
      key: 'name',
      width: 180,
    },
    {
      title: '适用位置',
      dataIndex: 'location',
      key: 'location',
      width: 180,
    },
    {
      title: '适用实验室',
      key: 'labs',
      width: 180,
      render: (record: Rule) => (
        <span>
          {record.labs?.map(lab => lab.simple_name).join(', ') || '未分配'}
        </span>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '状态',
      key: 'active',
      render: (record: Rule) => (
        <span>{record.active ? '启用' : '禁用'}</span>
      ),
      width: 80,
    },
    {
      title: '版本号',
      dataIndex: 'latest_version',  // 新增的分组列，显示 category
      key: 'latest_version',
      width: 180,
    },
    {
      title: '操作',
      key: 'action',
      render: (record: Rule) => (
        <>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingRule(record);
              setCurrentRule(record);
              fetchVersions(record.id);
              form.setFieldsValue({
                ...record,
                lab_ids: record.labs.map(lab => lab.id),
                active: record.active,  // 设置 active 字段
              });
              setModalVisible(true);
            }}
          >
            编辑
          </Button>
          <Button
            type="link"
            icon={record.active ? <StopOutlined /> : <CheckOutlined />}
            onClick={() => handleToggleActive(record)}
          >
            {record.active ? '禁用' : '启用'}
          </Button>
          <Popconfirm
            title="确定删除此规则吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="是"
            cancelText="否"
          >
            <Button type="link" icon={<DeleteOutlined />} danger>
              删除
            </Button>
          </Popconfirm>
          <Button
            type="link"
            icon={<BarChartOutlined />}
            onClick={() => {
              setEditingRule(record);
              handleShowStats(record.id, record.latest_version);
              
              fetchVersions(record.id);
            }}
          >
            统计
          </Button>
        </>
      ),
    },
  ];
  
  const handleCancel = () => {
    setModalVisible(false);
    setIsReadOnly(false);
    setPromptLoading(false)
    form.resetFields();
  }

  const fetchReports = async (page: number, size: number, version : number) => {
    try {
      setReportsLoading(true);
      const reportsRes = await api.get(`/api/review-rules/${statsData.rule_id}/reports/?page=${page}&page_size=${size}&version=${version}`);
      setReportsData(reportsRes.data.results);
    } catch (error) {
      message.error('获取审核报告失败');
      console.error(error);
    } finally {
      setReportsLoading(false);
    }
  }

  return (
    <Layout className="layout">
      <Header style={{ background: '#f0f2f5', padding: 0 }}>
        <TopNav />
      </Header>
      <Content>
        <Spin spinning={loading} tip="数据加载中...">
          <div style={{ padding: '20px' }}>
            <h2>审核规则</h2>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
                flexWrap: 'wrap',
              }}
            >
              {/* 左侧：筛选条件 */}
              <Space wrap>

                {/* 分类筛选 */}
                <Input
                  value={categoryInput}
                  placeholder="筛选规则分类"
                  style={{ width: 200 }}
                  onChange={(e) => setCategoryInput(e.target.value)}
                  onPressEnter={() => handleFilterChange('category', categoryInput)}
                />

                {/* 名称筛选 */}
                <Input
                  value={nameInput}
                  placeholder="筛选名称"
                  style={{ width: 200 }}
                  onChange={(e) => setNameInput(e.target.value)}
                  onPressEnter={() => handleFilterChange('name', nameInput)}
                />

                {/* 位置筛选 */}
                <Select
                  value={filters.location}
                  placeholder="筛选报告位置"
                  onChange={(value) => handleFilterChange('location', value)}
                  style={{ width: 200 }}
                >
                  <Select.Option value="">全部位置</Select.Option>
                  <Select.Option value="首页">首页</Select.Option>
                  <Select.Option value="正文">正文</Select.Option>
                  <Select.Option value="测试结果表格">测试结果表格</Select.Option>
                  <Select.Option value="样品页">样品页</Select.Option>
                  <Select.Option value="末页">末页</Select.Option>
                </Select>

                {/* 实验室筛选 */}
                <Select
                  // mode="multiple"
                  value={filters.lab_ids}
                  placeholder="筛选实验室"
                  onChange={(value) => handleFilterChange('lab_ids', [value])}
                  style={{ width: 300 }}
                >
                  {labs.map((lab) => (
                    <Select.Option key={lab.id} value={lab.id}>
                      {lab.simple_name + '/' + lab.lab_code}
                    </Select.Option>
                  ))}
                </Select>
                {/* 状态筛选 */}
                <Select
                  value={filters.active}
                  placeholder="筛选状态"
                  onChange={(value) => handleFilterChange('active', value)}
                  style={{ width: 200 }}
                >
                  <Select.Option value="">全部状态</Select.Option>
                  <Select.Option value="true">启用</Select.Option>
                  <Select.Option value="false">禁用</Select.Option>
                </Select>

                {/* 清空按钮 */}
                <Button
                  onClick={() => {
                    setFilters({
                      active: '',
                      category: '',
                      location: '',
                      name: '',
                      lab_ids: [],
                    });
                    setCategoryInput('');
                    setNameInput('');
                    fetchRules();
                  }}
                >
                  清空条件
                </Button>
              </Space>
              <Button
                type="primary"
                icon={<ExportOutlined  />}
                onClick={() => {
                  setShowExportModal(true);
                }}
              >
                导出打标统计
              </Button>

              {/* 右侧：添加规则按钮 */}
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingRule(null);
                  form.resetFields();
                  setModalVisible(true);
                }}
              >
                添加规则
              </Button>
            </div>
            
            <Table
              columns={columns}
              dataSource={rules}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
            <Modal
              title={statsData?.rule_name ? `规则统计：${statsData.rule_name}` : '规则统计'}
              visible={statsModalVisible}
              onCancel={() => {
                setStatsModalVisible(false);
                setStatsData(null);
                setReportsData([]);
                setCurrentPage(1);
                setPageSize(20);
              }}
              footer={null}
              width={800}
            >
              <Select
                style={{ width: 200, marginBottom: 16, marginTop: 16}} 
                placeholder="切换查看历史版本统计"
                optionFilterProp="children"
                defaultValue={-1}
                onChange={(value) => {
                  if (value === -1) {
                    // 切回当前版本
                    fetchReports(currentPage, pageSize, editingRule?.latest_version ?? 0);
                    handleShowStats(editingRule?.id ?? 0, editingRule?.latest_version ?? 0);
                  } else {
                    // 查看历史版本
                    fetchReports(currentPage, pageSize, value);
                    handleShowStats(editingRule?.id ?? 0, value);
                  }
                }}
              >
                {/* 默认项 */}
                <Select.Option key="default" value={-1}>
                  当前版本
                </Select.Option>
                {versions.map(version => (
                  <Select.Option key={version} value={version}>
                    {version}
                  </Select.Option>
                ))}
              </Select>
              <Spin spinning={reportsLoading}>
                {statsData && (
                  <Descriptions column={2} bordered size="small">
                    <Descriptions.Item label="总审核报告数">{statsData.total_reports}</Descriptions.Item>
                    <Descriptions.Item label="正确数量">{statsData.correct_count}</Descriptions.Item>
                    <Descriptions.Item label="错误数量">{statsData.incorrect_count}</Descriptions.Item>
                    <Descriptions.Item label="准确率">{statsData.accuracy ? (statsData.accuracy * 100).toFixed(0) + '%' : '-'}</Descriptions.Item>
                    <Descriptions.Item label="最近使用时间">{statsData.last_used_at || '-'}</Descriptions.Item>
                  </Descriptions>
                )}

                <h4 style={{ marginTop: 16 }}>审核报告</h4>
                <Table
                  size="small"
                  columns={[
                    { title: '报告编号', dataIndex: 'report_number', key: 'report_number' },
                    { title: '审核日期', dataIndex: 'review_date', key: 'review_date' },
                    { title: '判断结果', dataIndex: 'result_judgement', key: 'result_judgement' },
                    { title: '原因', dataIndex: 'reason', key: 'reason' },
                    { title: '操作', key: 'action', render: (record: ReportStat) => (
                        <Button type="link" onClick={() => {
                          const ruleParam = record.rule ? `&rule=${record.rule}` : '';
                          navigate(`/audit-detail?id=${record.report}${ruleParam}`);
                        }}>查看报告</Button>
                      )}
                  ]}
                  dataSource={reportsData}
                  rowKey="id"
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalReports,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    onChange: (page, size) => {
                      setCurrentPage(page);
                      setPageSize(size);
                      fetchReports(page, size, statsData.version); // 调用接口重新获取数据
                    },
                  }}
                />
              </Spin>
            </Modal>
            <Modal
              title={editingRule ? '编辑规则' : '添加规则'}
              visible={modalVisible}
              onCancel={() => handleCancel()}
              onOk={() => form.submit()}
              footer={[
              <Button key="cancel" onClick={handleCancel}>
                取消
              </Button>,
              <Button
                key="submit"
                type="primary"
                onClick={() => form.submit()}
                disabled={isReadOnly || promptLoading} // ⚠️ 根据状态禁用
              >
                保存
              </Button>,
            ]}
            >
              <Form
                form={form}
                onFinish={handleAddOrEdit}
                layout="vertical"
              >
                <Form.Item
                  label="切换历史版本"
                  name="version"
                >
                  <Select
                    placeholder="请选择版本号"
                    optionFilterProp="children"
                    onChange={(value) => {fetchRuleSnapshot(editingRule?.id || 0, value)}}
                  >
                    {/* 默认项 */}
                    <Select.Option key="0" value="0">
                      当前版本
                    </Select.Option>
                    {versions.map(version => (
                      <Select.Option key={version} value={version}>
                        {version}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="分组"
                  name="category"
                  rules={[{ required: true, message: '请输入规则名称' }]}
                >
                  <Input disabled={isReadOnly}/>
                </Form.Item>
                <Form.Item
                  label="规则名称"
                  name="name"
                  rules={[{ required: true, message: '请输入规则名称' }]}
                >
                  <Input disabled={isReadOnly}/>
                </Form.Item>
                <Form.Item
                  label="描述"
                  name="description"
                  rules={[{ required: true, message: '请输入规则描述' }]}
                >
                  <Input.TextArea rows={3} disabled={isReadOnly}/>
                </Form.Item>
                <Form.Item
                  label="实验室"
                  name="lab_ids"
                  rules={[{ required: true, message: '请至少选择一个实验室' }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="请选择实验室（可多选）"
                    optionFilterProp="children"
                    disabled={isReadOnly}
                  >
                    {labs.map(lab => (
                      <Select.Option key={lab.id} value={lab.id}>
                        {lab.simple_name + '/' + lab.lab_code}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Spin spinning={promptLoading} tip="提示词生成中...">
                  <Form.Item className='prompt-item'
                    label={
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                          paddingRight: 8  // ✅ 可调
                        }}>
                        <div>提示词</div>
                        <Button
                          type="link"
                          onClick={handleGeneratePrompt}
                          style={{ padding: 0 }}
                        >
                          生成提示词
                        </Button>
                      </div>
                    }
                    name="prompt"
                    rules={[{ required: true, message: '请输入提示词' }]}
                  >
                    <Input.TextArea rows={5} disabled={isReadOnly}/>
                  </Form.Item>
                </Spin>
                <Form.Item
                  label="报告位置"
                  name="location"
                  rules={[{ required: false, message: '请选择规则应用的报告位置' }]}
                >
                  <Select placeholder="请选择报告位置" disabled={isReadOnly}>
                    <Select.Option value="首页">首页</Select.Option>
                    <Select.Option value="正文">正文</Select.Option>
                    <Select.Option value="测试结果表格">测试结果表格</Select.Option>
                    <Select.Option value="样品页">样品页</Select.Option>
                    <Select.Option value="末页">末页</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item label="启用状态" name="active" valuePropName="checked">
                  <Switch disabled = {isReadOnly}/>
                </Form.Item>
              </Form>
            </Modal>
            <ExportStatisticsModal
              open={showExportModal}
              onCancel={() => setShowExportModal(false)}
              withCredentials={true}
            />
          </div>
        </Spin>
      </Content>
    </Layout>
  );
};

export default ReviewRules;
